import axios, { type AxiosResponse } from 'axios';
import type { AyahData, EvaluateResponse, Session, Stats } from '../types';

// In-flight request dedupe — prevents React strict-mode double-fetches
// from doubling our LLM/TTS costs.
const _inflight = new Map<string, Promise<unknown>>();

function _dedupedRequest<T>(key: string, factory: () => Promise<T>): Promise<T> {
  const existing = _inflight.get(key);
  if (existing) return existing as Promise<T>;
  const promise = factory().finally(() => _inflight.delete(key));
  _inflight.set(key, promise);
  return promise;
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

function transformKeys(data: unknown): unknown {
  if (Array.isArray(data)) {
    return data.map(transformKeys);
  }
  if (data !== null && typeof data === 'object' && !(data instanceof Blob) && !(data instanceof File)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      result[snakeToCamel(key)] = transformKeys(value);
    }
    return result;
  }
  return data;
}

let _getToken: (() => Promise<string | null>) | null = null;

export function setGetToken(fn: () => Promise<string | null>) {
  _getToken = fn;
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    Accept: 'application/json',
  },
});

api.interceptors.request.use(async (config) => {
  if (_getToken) {
    try {
      const token = await _getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // No auth available, continue without token
    }
  }
  return config;
});

api.interceptors.response.use((response: AxiosResponse) => {
  if (response.data && typeof response.data === 'object') {
    response.data = transformKeys(response.data);
  }
  return response;
});

export async function fetchAyah(surah: number, ayah: number): Promise<AyahData> {
  const response = await api.get<AyahData>(`/ayah/${surah}/${ayah}`);
  return response.data;
}

export interface SurahData {
  surah: number;
  surahName: string;
  totalAyahs: number;
  ayahs: AyahData[];
}

export async function fetchSurah(surah: number): Promise<SurahData> {
  const response = await api.get<SurahData>(`/ayah/surah/${surah}`);
  return response.data;
}

export async function evaluateRecitation(
  audioBlob: Blob,
  surah: number,
  ayah: number,
  userLevel: string = 'intermediate',
  language: string = 'en'
): Promise<EvaluateResponse> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append('surah', String(surah));
  formData.append('ayah', String(ayah));
  formData.append('user_level', userLevel);
  formData.append('language', language);
  formData.append('gemini_model', localStorage.getItem('tajweed_gemini_model') || 'gemini-3.1-pro-preview');

  const response = await api.post<EvaluateResponse>('/evaluate/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return response.data;
}

export async function fetchDrills(
  findings: Record<string, unknown>[],
  userLevel: string = 'intermediate',
  signal?: AbortSignal
): Promise<{ drills: import('../types').Drill[] }> {
  const key = `drills|${userLevel}|${JSON.stringify(findings).slice(0, 200)}`;
  return _dedupedRequest(key, async () => {
    const response = await api.post<{ drills: import('../types').Drill[] }>('/evaluate/drills', {
      findings,
      user_level: userLevel,
    }, { timeout: 30000, signal });
    return response.data;
  });
}

export async function fetchExplanationAudio(
  findings: Record<string, unknown>[],
  praise: string[] = [],
  score: number = 0,
  language: string = 'en',
  signal?: AbortSignal
): Promise<string> {
  const key = `explain-audio|${language}|${score}|${JSON.stringify(findings).slice(0, 200)}`;
  return _dedupedRequest(key, async () => {
    const response = await api.post('/evaluate/explain-audio', {
      findings,
      praise,
      score,
      language,
    }, { timeout: 30000, responseType: 'blob', signal });
    return URL.createObjectURL(response.data as Blob);
  });
}

export async function fetchFindingAudio(
  finding: Record<string, unknown>,
  surah: number = 1,
  ayah: number = 1,
  language: string = 'en',
  signal?: AbortSignal,
  // Layer 2 — Audio contrast: pass the student's recording + word_timings so the
  // backend can splice the user's own attempt of the word alongside the sheikh's.
  studentAudioBlob?: Blob | null,
  wordTimings?: Array<{ word: string; start: number; end: number }> | null,
): Promise<string> {
  const findingWithRef = { ...finding, surah, ayah };
  // Frontend kill switch — set localStorage.tajweed_audio_contrast = '0' to disable
  const contrastEnabled =
    (typeof localStorage !== 'undefined' && localStorage.getItem('tajweed_audio_contrast') !== '0');
  const useContrast = !!(contrastEnabled && studentAudioBlob && wordTimings && wordTimings.length > 0);

  const key = `explain-finding|${surah}:${ayah}|${language}|${useContrast ? 'L2' : 'L1'}|${JSON.stringify(finding).slice(0, 200)}`;
  return _dedupedRequest(key, async () => {
    // Always use multipart now — the backend reads JSON-encoded `finding` and
    // optionally a `student_audio` file + `word_timings` JSON string.
    const formData = new FormData();
    formData.append('finding', JSON.stringify(findingWithRef));
    formData.append('language', language);
    if (useContrast) {
      formData.append('student_audio', studentAudioBlob as Blob, 'student.webm');
      formData.append('word_timings', JSON.stringify(wordTimings));
    }
    const response = await api.post('/evaluate/explain-finding', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
      responseType: 'blob',
      signal,
    });
    return URL.createObjectURL(response.data as Blob);
  });
}

export type PracticeStatus =
  | 'attempted'
  | 'silence'
  | 'humming'
  | 'wrong_language'
  | 'wrong_word'
  | 'unintelligible';

export interface PracticeResult {
  matchPct: number;
  verdict: 'perfect' | 'close' | 'needs_work';
  feedback: string;
  tryAgain: boolean;
  feedbackAudioB64?: string;
  status?: PracticeStatus;
  heard?: string;
}

export type PracticeDifficulty = 'easy' | 'normal' | 'strict';

export async function practiceWord(
  audioBlob: Blob,
  surah: number,
  ayah: number,
  targetWord: string,
  expectedFix: string = '',
  language: string = 'en',
  attemptNumber: number = 1,
  previousFeedback: string = '',
  difficulty: PracticeDifficulty = 'normal'
): Promise<PracticeResult> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'practice.webm');
  formData.append('surah', String(surah));
  formData.append('ayah', String(ayah));
  formData.append('target_word', targetWord);
  formData.append('expected_fix', expectedFix);
  formData.append('language', language);
  formData.append('attempt_number', String(attemptNumber));
  formData.append('previous_feedback', previousFeedback);
  formData.append('difficulty', difficulty);

  const response = await api.post<PracticeResult>('/evaluate/practice-word', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return response.data;
}

export function getSheikhWordAudioUrl(surah: number, ayah: number, word: string): string {
  const params = new URLSearchParams({ surah: String(surah), ayah: String(ayah), word });
  return `${api.defaults.baseURL}/evaluate/sheikh-word?${params}`;
}

export async function fetchSessions(
  limit: number = 5,
  offset: number = 0,
): Promise<{ items: Session[]; total: number }> {
  const response = await api.get<{ items: Session[]; total: number }>('/progress/sessions', {
    params: { limit, offset },
  });
  return response.data;
}

export async function fetchStats(): Promise<Stats> {
  const response = await api.get<Stats>('/progress/stats');
  return response.data;
}

/**
 * Fire-and-forget Gemini cache warm-up for an ayah. Called as soon as the
 * user navigates to an ayah so that when they hit record, the eval gets
 * a cache HIT instead of paying the ~1-2s cache-create cost on the critical path.
 *
 * Errors are swallowed — this is a pure optimization; if the warm-up fails
 * the eval will just create the cache itself on first request.
 */
export function precacheAyah(surah: number, ayah: number): void {
  const model = localStorage.getItem('tajweed_gemini_model') || 'gemini-3.1-pro-preview';
  api
    .post('/evaluate/precache', { surah, ayah, model })
    .catch(() => { /* silent — non-critical */ });
}

export async function createCheckoutSession(): Promise<{ checkoutUrl: string }> {
  const response = await api.post<{ checkoutUrl: string }>('/subscription/checkout');
  return response.data;
}

export async function createPortalSession(): Promise<{ portalUrl: string }> {
  const response = await api.post<{ portalUrl: string }>('/subscription/portal');
  return response.data;
}

export async function getSubscriptionStatus(): Promise<{ status: string; isPro: boolean }> {
  const response = await api.get<{ status: string; isPro: boolean }>('/subscription/status');
  return response.data;
}

export async function verifyCheckoutSession(sessionId: string): Promise<{ activated: boolean; isPro: boolean }> {
  const response = await api.post<{ activated: boolean; isPro: boolean }>('/subscription/verify', null, {
    params: { session_id: sessionId },
  });
  return response.data;
}

// ---- Hifz (Memorize) — Progressive Reveal ----

export interface HifzTargetWord {
  index: number;
  text: string;
  ayah: number;
  wordInAyah: number;
}

export interface HifzTarget {
  surah: number;
  ayahStart: number;
  ayahEnd: number;
  surahName: string;
  words: HifzTargetWord[];
  ayahTexts: Record<number, string>;
}

export async function fetchHifzTarget(
  surah: number,
  ayahStart: number,
  ayahEnd?: number
): Promise<HifzTarget> {
  const response = await api.get<HifzTarget>('/hifz/target', {
    params: { surah, ayah_start: ayahStart, ayah_end: ayahEnd ?? ayahStart },
  });
  return response.data;
}

export interface HifzMatchResult {
  revealed: number[];
  missed: number[];
  newPointer: number;
  transcript: string;
}

export async function matchHifzAudio(
  audioBlob: Blob,
  pointer: number,
  targetWords: string[]
): Promise<HifzMatchResult> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'chunk.webm');
  formData.append('pointer', String(pointer));
  formData.append('target_words_json', JSON.stringify(targetWords));
  const response = await api.post<HifzMatchResult>('/hifz/match', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return response.data;
}

export interface LiveSessionHistoryItem {
  sessionId: string;
  title: string | null;
  surah: number;
  ayahStart: number;
  ayahEnd: number;
  totalTurns: number;
  averageScore: number;
  rulesPracticed: string[];
  durationS: number;
  createdAt: string;
}

export async function fetchLiveSessionHistory(limit: number = 20): Promise<LiveSessionHistoryItem[]> {
  const response = await api.get<LiveSessionHistoryItem[]>('/live-session/history', {
    params: { limit },
  });
  return response.data;
}

export interface LiveSessionDetail extends LiveSessionHistoryItem {
  messages: Array<{ role: string; text: string; score?: number }>;
}

export async function fetchLiveSessionDetail(sessionId: string): Promise<LiveSessionDetail> {
  const response = await api.get<LiveSessionDetail>(`/live-session/history/${sessionId}`);
  return response.data;
}

export async function renameLiveSession(sessionId: string, title: string): Promise<void> {
  await api.patch(`/live-session/history/${sessionId}/rename`, null, {
    params: { title },
  });
}

export async function deleteLiveSession(sessionId: string): Promise<void> {
  await api.delete(`/live-session/history/${sessionId}`);
}

export async function submitContactForm(
  name: string,
  email: string,
  message: string,
): Promise<void> {
  await api.post('/contact', { name, email, message }, { timeout: 15000 });
}

export default api;
